import { useEffect, useId, useRef, useState } from 'react';
import type { NotaOS } from '@/features/pos-venda/api/avaliacoesApi';
import { EstrelaNota } from '@/features/pos-venda/components/EstrelaNota';

const MAX_RESUMO = 50;

/**
 * Comentário em poucas palavras (corta no último espaço) ou "Sem comentário".
 * O servidor já manda o trecho encurtado (terminando em "…" se cortou).
 */
export function resumirComentario(comentario: string | null | undefined, max = MAX_RESUMO): string {
  const bruto = (comentario ?? '').replace(/\s+/g, ' ').trim();
  const jaCortado = bruto.endsWith('…');
  const t = jaCortado ? bruto.slice(0, -1).trimEnd() : bruto;
  if (!t) return 'Sem comentário';
  if (t.length <= max) return jaCortado ? `${t}…` : t;
  const corte = t.slice(0, max);
  const ultimoEspaco = corte.lastIndexOf(' ');
  return `${(ultimoEspaco > max * 0.6 ? corte.slice(0, ultimoEspaco) : corte).trimEnd()}…`;
}

/**
 * Estrela pequena com a nota no card do kanban. Passar o mouse ou focar
 * pelo teclado mostra o comentário em poucas palavras; no toque, um tap
 * abre/fecha o mesmo balão sem abrir a O.S. (o clique não sobe para o card)
 * e sem atrapalhar o arrastar — nada aqui intercepta eventos de drag.
 */
export function NotaAvaliacaoKanban({ avaliacao }: { avaliacao: NotaOS }) {
  const [aberto, setAberto] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  const idTooltip = useId();
  const texto = resumirComentario(avaliacao.trecho);

  // Balão aberto por toque: fecha ao tocar fora ou com Esc.
  useEffect(() => {
    if (!aberto) return;
    const fora = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setAberto(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAberto(false);
    };
    document.addEventListener('pointerdown', fora);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('pointerdown', fora);
      document.removeEventListener('keydown', esc);
    };
  }, [aberto]);

  return (
    <span ref={ref} className={`kanban-nota${aberto ? ' kanban-nota--aberta' : ''}`}>
      <button
        type="button"
        className="kanban-nota__botao"
        aria-label={`Avaliação do cliente: nota ${avaliacao.nota} de 5`}
        aria-describedby={idTooltip}
        aria-expanded={aberto}
        onClick={(e) => {
          e.stopPropagation();
          setAberto((v) => !v);
        }}
        onBlur={() => setAberto(false)}
      >
        <EstrelaNota nota={avaliacao.nota} tamanho={26} />
      </button>
      <span id={idTooltip} role="tooltip" className="kanban-nota__tooltip">
        {texto}
      </span>
    </span>
  );
}
